import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash, Users, EnvelopeSimple, ShieldCheck, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { DataTable } from "../components/DataTable";

const ROLES = ["admin", "accountant", "sales", "purchasing", "viewer"];
const ROLE_COLOR = {
  admin: "bg-primary/10 text-primary border-primary/20",
  accountant: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sales: "bg-blue-50 text-blue-700 border-blue-200",
  purchasing: "bg-amber-50 text-amber-700 border-amber-200",
  viewer: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function Team() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales" });

  const { data: users = [], isError } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: user?.role === "admin",
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/users/invite", form)).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false); setForm({ email: "", name: "", role: "sales" });
      toast.success(res.email_sent ? `Invite emailed to ${res.user.email}` : "User created (invite email failed to send)");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const changeRole = useMutation({
    mutationFn: async ({ id, role }) => api.put(`/users/${id}/role`, { role }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Role updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const resend = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/resend-invite`)).data,
    onSuccess: (res) => toast.success(res.email_sent ? "Invite email re-sent" : "New link created (email failed to send)"),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (user?.role !== "admin") {
    return (
      <div className="animate-fade-up border border-border bg-card p-10 text-center">
        <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-bold">Administrators only</h2>
        <p className="text-sm text-muted-foreground mt-1">You need the admin role to manage team members.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <SectionHeader title="Team & Roles" subtitle="User management">
        <Button onClick={() => setOpen(true)} data-testid="invite-user-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
          <UserPlus size={16} weight="bold" className="mr-1" /> Invite Teammate
        </Button>
      </SectionHeader>

      <DataTable
        testid="users-table"
        data={users}
        rowKey={(u) => u.user_id}
        rowTestid="user-row"
        empty={isError ? "Could not load users." : "No team members yet."}
        columns={[
          { key: "name", header: "Name", filter: "text", cellClassName: "text-sm font-medium", cell: (u) => <>{u.name}{u.user_id === user.user_id && <span className="text-xs text-muted-foreground ml-1">(you)</span>}</> },
          { key: "email", header: "Email", filter: "text", cellClassName: "text-sm text-muted-foreground" },
          { key: "role", header: "Role", filter: "select", options: ROLES, cell: (u) => (
            u.user_id === user.user_id ? (
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold border rounded-sm ${ROLE_COLOR[u.role]}`}>{u.role}</span>
            ) : (
              <Select value={u.role} onValueChange={(role) => changeRole.mutate({ id: u.user_id, role })}>
                <SelectTrigger className="h-8 rounded-sm w-36 text-xs" data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            )
          ) },
          { key: "status", header: "Status", filter: "select", options: ["Active", "Invite pending"], accessor: (u) => (u.pending ? "Invite pending" : "Active"), cell: (u) => <span className={`text-xs font-semibold ${u.pending ? "text-amber-600" : "text-emerald-600"}`}>{u.pending ? "Invite pending" : "Active"}</span> },
          { key: "actions", header: "", sortable: false, align: "right", cell: (u) => (
            u.user_id !== user.user_id ? (
              <div className="flex items-center justify-end gap-0.5">
                {u.pending && (
                  <button onClick={() => resend.mutate(u.user_id)} disabled={resend.isPending} data-testid="resend-invite" title="Re-send invite email" className="p-1.5 text-muted-foreground hover:text-primary disabled:opacity-40 transition-colors duration-150"><PaperPlaneTilt size={16} weight="bold" /></button>
                )}
                <button onClick={() => del.mutate(u.user_id)} data-testid="delete-user" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={16} /></button>
              </div>
            ) : null
          ) },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users size={20} className="text-primary" /> Invite Teammate</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="overline-label">Email</Label><Input data-testid="invite-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" placeholder="teammate@company.com" /></div>
            <div><Label className="overline-label">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" placeholder="Jane Doe" /></div>
            <div>
              <Label className="overline-label">Role</Label>
              <Select value={form.role} onValueChange={(role) => setForm({ ...form, role })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-1.5"><EnvelopeSimple size={14} className="mt-0.5 shrink-0" /> An email invite with a secure set-password link (valid 72h) will be sent.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => invite.mutate()} disabled={!form.email || invite.isPending} data-testid="send-invite-button" className="rounded-sm bg-primary hover:bg-primary/90">{invite.isPending ? "Sending…" : "Send Invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
